import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./pages/Login";
import MfaSetup from "./pages/MfaSetup";
import Register from "./pages/Register";
import SetupAccount from "./pages/SetupAccount";
import PlatformRegistrations from "./pages/PlatformRegistrations";
import ForcePasswordReset from "./pages/ForcePasswordReset";
import AdminDashboard from "./pages/AdminDashboard";
import AttorneyUpload from "./pages/AttorneyUpload";
import InmateDocuments from "./pages/InmateDocuments";
import AdminInmates from "./pages/AdminInmates";
import AttorneyAccess from "./pages/AttorneyAccess";
import AdminAttorneyAccess from "./pages/AdminAttorneyAccess";
import AttorneyInmateAccess from "./pages/AttorneyInmateAccess";
import AdminInmateAccess from "./pages/AdminInmateAccess";
import AdminAuditLogs from "./pages/AdminAuditLogs";
import SessionTimeout from "./components/SessionTimeout";
import AttorneyDocuments from "./pages/AttorneyDocuments";
import Landing from "./pages/Landing";
import Security from "./pages/Security";
import FacilitySettings from "./pages/FacilitySettings";
import AttorneyInbox from "./pages/AttorneyInbox";
import InmateUpload from "./pages/InmateUpload";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PlatformSupport from "./pages/PlatformSupport";
import LegalPolicy from "./pages/LegalPolicy";
import Pricing from "./pages/Pricing";
import AcademyPublic from "./pages/AcademyPublic";
import AcademyAuthenticated from "./pages/AcademyAuthenticated";
import RequestDemo from "./pages/RequestDemo";
import DemoDashboard from "./pages/DemoDashboard";
import PlatformDemos from "./pages/PlatformDemos";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import { getStoredUser, homeForRole } from "./utils/session";

export function protectedRouteRedirect(user, roles) {
  if (!user) {
    return "/login";
  }

  if (roles && !roles.includes(user.role)) {
    return "/login";
  }

  if (user.must_reset_password) {
    return "/password/reset-required";
  }

  if (["platform_admin", "facility_admin", "admin", "attorney"].includes(user.role) && !user.mfa_enabled && !user.demo_mfa_exempt) {
    return "/mfa/setup";
  }

  return null;
}

export function ProtectedRoute({ children, roles }) {
  const redirect = protectedRouteRedirect(getStoredUser(), roles);
  if (redirect) return <Navigate to={redirect} replace />;

  return children;
}

export function MfaSetupRoute() {
  const user = getStoredUser();
  const location = useLocation();
  const productionSetup = new URLSearchParams(location.search).get('production_setup') === '1';
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <Navigate to="/password/reset-required" replace />;
  if (!productionSetup && (user.mfa_enabled || user.role === "inmate" || user.demo_mfa_exempt)) return <Navigate to={homeForRole(user.role, user)} replace />;
  return <MfaSetup />;
}

export function ForcedResetRoute() {
  const user = getStoredUser();
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_reset_password) {
    return <Navigate to={user.mfa_enabled || user.role === "inmate" || user.demo_mfa_exempt ? homeForRole(user.role, user) : "/mfa/setup"} replace />;
  }
  return <ForcePasswordReset />;
}

function App() {
  return (
    <BrowserRouter>
      <SessionTimeout />
      <Routes>
	<Route path="/" element={<Landing />} />
	<Route path="/security" element={<Security />} />
	<Route path="/pricing" element={<Pricing />} />
	<Route path="/request-demo" element={<RequestDemo />} />
	<Route path="/academy" element={<AcademyPublic />} />
	<Route path="/academy/:topic" element={<AcademyPublic />} />
        <Route path="/legal/:policy" element={<LegalPolicy />} />
        <Route path="/login" element={<Login />} />
	<Route path="/password/forgot" element={<ForgotPassword />} />
	<Route path="/password/reset" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/setup-account" element={<SetupAccount />} />
	<Route
	  path="/admin/settings"
	  element={
	    <ProtectedRoute roles={["facility_admin", "admin"]}>
	      <FacilitySettings />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/admin/audit"
	  element={
	    <ProtectedRoute roles={["platform_admin", "facility_admin", "admin"]}>
	      <AdminAuditLogs />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/mfa/setup"
	  element={<MfaSetupRoute />}
	/>
        <Route
          path="/password/reset-required"
          element={
            <ForcedResetRoute />
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={["facility_admin", "admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
	<Route
	  path="/inmate/documents"
	  element={
	    <ProtectedRoute roles={["inmate"]}>
	      <InmateDocuments />
	    </ProtectedRoute>
	  }
	/>
	  <Route
	  path="/attorney/inbox"
	  element={
	    <ProtectedRoute roles={["attorney"]}>
	      <AttorneyInbox />
	    </ProtectedRoute>
	  }
	/>

	<Route
	  path="/inmate/upload"
	  element={
	    <ProtectedRoute roles={["inmate"]}>
	      <InmateUpload />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/attorney/documents"
	  element={
	    <ProtectedRoute roles={["attorney"]}>
	      <AttorneyDocuments />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/attorney/upload"
	  element={
	    <ProtectedRoute roles={["attorney"]}>
	      <AttorneyUpload />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/admin/inmates"
	  element={
	    <ProtectedRoute roles={["facility_admin", "admin"]}>
	      <AdminInmates />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/attorney/access"
	  element={
	    <ProtectedRoute roles={["attorney"]}>
	      <AttorneyAccess />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/admin/attorney-access"
	  element={
	    <ProtectedRoute roles={["facility_admin", "admin"]}>
	      <AdminAttorneyAccess />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/attorney/inmate-access"
	  element={
	    <ProtectedRoute roles={["attorney"]}>
	      <AttorneyInmateAccess />
	    </ProtectedRoute>
	  }
	/>
	<Route
	  path="/admin/inmate-access"
	  element={
	    <ProtectedRoute roles={["facility_admin", "admin"]}>
	      <AdminInmateAccess />
	    </ProtectedRoute>
	  }
	/>
        <Route
          path="/platform/registrations"
          element={
            <ProtectedRoute roles={["platform_admin"]}>
              <PlatformRegistrations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/platform/support"
          element={
            <ProtectedRoute roles={["platform_admin"]}>
              <PlatformSupport />
            </ProtectedRoute>
          }
        />
        <Route path="/app/academy" element={<ProtectedRoute><AcademyAuthenticated /></ProtectedRoute>} />
        <Route path="/app/academy/facility-admin" element={<ProtectedRoute roles={["facility_admin", "admin", "platform_admin"]}><AcademyAuthenticated guide="facility-admin" /></ProtectedRoute>} />
        <Route path="/app/academy/attorney" element={<ProtectedRoute roles={["attorney", "platform_admin"]}><AcademyAuthenticated guide="attorney" /></ProtectedRoute>} />
        <Route path="/app/academy/inmate" element={<ProtectedRoute roles={["inmate", "platform_admin"]}><AcademyAuthenticated guide="inmate" /></ProtectedRoute>} />
        <Route path="/app/academy/platform-admin" element={<ProtectedRoute roles={["platform_admin"]}><AcademyAuthenticated guide="platform-admin" /></ProtectedRoute>} />
        <Route path="/platform/demos" element={<ProtectedRoute roles={["platform_admin"]}><PlatformDemos /></ProtectedRoute>} />
        <Route path="/app/demo" element={<ProtectedRoute roles={["facility_admin"]}><DemoDashboard /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
